import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Check, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errMsg } from "@/lib/api";
import { AdminCard, PageTitle } from "@/pages/admin/AdminLayout";
import { Stars } from "@/components/site/Stars";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const cls = "w-full h-11 px-4 rounded-xl border border-sand bg-white text-sm text-ocean focus:outline-none focus:border-sea";
const SRC = { site: "Sito", google: "Google", tripadvisor: "Tripadvisor" };
const ST = { pending: "Da approvare", approved: "Pubblicata", hidden: "Nascosta" };
const ST_PILL = { pending: "bg-amber-50 text-amber-800 border-amber-200", approved: "bg-sage-mist text-sage-dark border-sage-light", hidden: "bg-ocean/5 text-ocean/60 border-ocean/10" };
const empty = { name: "", country: "", rating: 5, text: "", tour_slug: "none", source: "site", status: "approved", date: "" };

const F = ({ label, children, className = "" }) => (
  <div className={className}><label className="block text-[11px] tracking-[0.18em] uppercase text-ocean/55 mb-2">{label}</label>{children}</div>
);

export default function AdminReviews() {
  const [list, setList] = useState([]);
  const [tours, setTours] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => adminApi.get("/admin/reviews").then((r) => setList(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); adminApi.get("/admin/tours").then((r) => setTours(r.data)); }, []);

  const payload = (r) => ({ name: r.name, country: r.country || "", rating: Number(r.rating), text: r.text, tour_slug: r.tour_slug === "none" ? "" : r.tour_slug || "", source: r.source, status: r.status, date: r.date || "" });

  const setStatus = async (r, status) => {
    try {
      await adminApi.put(`/admin/reviews/${r.id}`, payload({ ...r, status }));
      setList((l) => l.map((x) => (x.id === r.id ? { ...x, status } : x)));
      toast.success(status === "approved" ? "Recensione pubblicata" : "Recensione nascosta");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Eliminare la recensione di ${r.name}?`)) return;
    try { await adminApi.delete(`/admin/reviews/${r.id}`); setList((l) => l.filter((x) => x.id !== r.id)); toast.success("Recensione eliminata"); } catch (e) { toast.error(errMsg(e)); }
  };

  const save = async () => {
    if (!edit.name.trim() || edit.text.trim().length < 3) return toast.error("Nome e testo sono obbligatori");
    setSaving(true);
    try {
      if (edit.id) await adminApi.put(`/admin/reviews/${edit.id}`, payload(edit));
      else await adminApi.post("/admin/reviews", payload(edit));
      toast.success("Recensione salvata");
      setEdit(null);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const tourName = (slug) => tours.find((x) => x.slug === slug)?.translations?.it?.title || tours.find((x) => x.slug === slug)?.translations?.en?.title || "";
  const shown = filter === "all" ? list : list.filter((r) => r.status === filter);
  const pending = list.filter((r) => r.status === "pending").length;

  return (
    <div data-testid="admin-reviews">
      <PageTitle title="Recensioni" sub="Approva quelle inviate dai clienti, aggiungi quelle ricevute su Google o Tripadvisor."
        right={<button onClick={() => setEdit({ ...empty })} className="inline-flex items-center gap-2 px-5 py-3 bg-ocean text-ivory text-sm font-medium rounded-xl btn-lift" data-testid="admin-review-new"><Plus size={16} />Aggiungi recensione</button>} />
      <Tabs value={filter} onValueChange={setFilter} className="mb-6">
        <TabsList className="bg-white border border-sand">
          <TabsTrigger value="all" data-testid="admin-reviews-tab-all">Tutte ({list.length})</TabsTrigger>
          <TabsTrigger value="pending" data-testid="admin-reviews-tab-pending">Da approvare ({pending})</TabsTrigger>
          <TabsTrigger value="approved" data-testid="admin-reviews-tab-approved">Pubblicate</TabsTrigger>
          <TabsTrigger value="hidden" data-testid="admin-reviews-tab-hidden">Nascoste</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="space-y-4">
        {loading && <AdminCard className="p-8 text-center text-ocean/45">Caricamento...</AdminCard>}
        {!loading && shown.length === 0 && <AdminCard className="p-10 text-center text-ocean/50" data-testid="admin-reviews-empty">Nessuna recensione in questa sezione.</AdminCard>}
        {shown.map((r) => (
          <AdminCard key={r.id} className="p-5 md:p-6" data-testid={`admin-review-${r.id}`}>
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Stars value={r.rating} size={14} />
                  <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium border ${ST_PILL[r.status]}`}>{ST[r.status]}</span>
                  <span className="px-2.5 py-0.5 rounded-full text-[11px] border border-sand text-ocean/60">{SRC[r.source]}</span>
                  {r.sample && <span className="px-2.5 py-0.5 rounded-full text-[11px] border border-sea/30 text-sea" data-testid="admin-review-sample-badge">Esempio: sostituiscila con una vera</span>}
                </div>
                <p className="mt-3 text-ocean leading-relaxed">“{r.text}”</p>
                <p className="mt-2 text-xs text-ocean/55">{r.name}{r.country ? ` · ${r.country}` : ""} · {r.date}{r.tour_slug ? ` · ${tourName(r.tour_slug)}` : ""}{r.email ? ` · ${r.email}` : ""}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {r.status !== "approved" && <button onClick={() => setStatus(r, "approved")} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-ocean text-ivory text-xs btn-lift" data-testid={`admin-review-approve-${r.id}`}><Check size={14} />Pubblica</button>}
                {r.status === "approved" && <button onClick={() => setStatus(r, "hidden")} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-sand text-ocean text-xs" data-testid={`admin-review-hide-${r.id}`}><EyeOff size={14} />Nascondi</button>}
                <button onClick={() => setEdit({ ...r, tour_slug: r.tour_slug || "none" })} aria-label="Modifica" className="p-2 text-ocean/60 hover:text-ocean" data-testid={`admin-review-edit-${r.id}`}><Pencil size={16} /></button>
                <button onClick={() => remove(r)} aria-label="Elimina" className="p-2 text-rose-700" data-testid={`admin-review-delete-${r.id}`}><Trash2 size={16} /></button>
              </div>
            </div>
          </AdminCard>
        ))}
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-white sm:max-w-xl rounded-2xl" data-testid="admin-review-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-ocean">{edit?.id ? "Modifica recensione" : "Nuova recensione"}</DialogTitle>
            <DialogDescription>Puoi copiare qui le recensioni ricevute su Google o Tripadvisor.</DialogDescription>
          </DialogHeader>
          {edit && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <F label="Nome"><input className={cls} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="admin-review-name" /></F>
              <F label="Paese"><input className={cls} value={edit.country || ""} onChange={(e) => setEdit({ ...edit, country: e.target.value })} data-testid="admin-review-country" /></F>
              <F label="Voto">
                <Select value={String(edit.rating)} onValueChange={(v) => setEdit({ ...edit, rating: Number(v) })}>
                  <SelectTrigger className="h-11 rounded-xl border-sand bg-white" data-testid="admin-review-rating"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{[5, 4, 3, 2, 1].map((n) => <SelectItem key={n} value={String(n)}>{n} stelle</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Fonte">
                <Select value={edit.source} onValueChange={(v) => setEdit({ ...edit, source: v })}>
                  <SelectTrigger className="h-11 rounded-xl border-sand bg-white" data-testid="admin-review-source"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{Object.entries(SRC).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Tour" className="md:col-span-2">
                <Select value={edit.tour_slug || "none"} onValueChange={(v) => setEdit({ ...edit, tour_slug: v })}>
                  <SelectTrigger className="h-11 rounded-xl border-sand bg-white" data-testid="admin-review-tour"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white max-h-72"><SelectItem value="none">Nessun tour specifico</SelectItem>{tours.map((x) => <SelectItem key={x.slug} value={x.slug}>{x.translations?.it?.title || x.translations?.en?.title}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Data"><input type="date" className={cls} value={edit.date || ""} onChange={(e) => setEdit({ ...edit, date: e.target.value })} data-testid="admin-review-date" /></F>
              <F label="Stato">
                <Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}>
                  <SelectTrigger className="h-11 rounded-xl border-sand bg-white" data-testid="admin-review-status"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{Object.entries(ST).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Testo" className="md:col-span-2"><textarea rows={4} className="w-full px-4 py-3 rounded-xl border border-sand bg-white text-sm text-ocean focus:outline-none focus:border-sea" value={edit.text} onChange={(e) => setEdit({ ...edit, text: e.target.value })} data-testid="admin-review-text" /></F>
              <div className="md:col-span-2 flex justify-end gap-3 pt-2">
                <button onClick={() => setEdit(null)} className="px-5 py-3 rounded-xl border border-sand text-sm text-ocean" data-testid="admin-review-cancel">Annulla</button>
                <button onClick={save} disabled={saving} className="px-6 py-3 rounded-xl bg-ocean text-ivory text-sm font-medium disabled:opacity-60 btn-lift" data-testid="admin-review-save">{saving ? "Salvataggio..." : "Salva"}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
